import { useEffect, useMemo, useState } from 'react';

import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import { DropdownMenu } from '../../form';
import { Loading, Tag } from '../../UI';
import { SearchAssetSuggestions } from '../SearchAssetSuggestions';
import ScrollArrows from '../Contribution/ScrollArrows';

import {
  GET_FILTERED_ASSETS,
  GET_NEWS,
  WIDGET_REDDIT,
  WIDGET_TWITTER,
  WIDGET_YOUTUBE_SEARCH,
} from '../../../http/apollo-queries';

import {
  DataNews,
  DataReddit,
  DataTwitter,
  DataYoutube,
  SocialTag,
} from './types';

import { NewsCard } from './NewsCard';
import { IAsset } from '../../../types/model';
import { NoSearchResults } from '../../NoSearchResults';
import { GridWidget } from '../types';
import { TwitterCard } from './TwitterCard';
import { YoutubeCard } from './YoutubeCard';
import { RedditCard } from './RedditCard';
import { useLocation, useParams } from 'react-router-dom';
import { useUserStrategies } from '../../../context/UserStrategies';
import { MarketTechnicalIndicator } from '../../Market/Widgets/MarketTechnicalIndicator';
import SearchInput from '../../Search-input/Search-input';

const SEARCH_TYPE = ['PORTFOLIO', 'INSPIRED', 'FAVORITES'] as const;
const SECOND = 1000;
const MINUTE = 60 * SECOND;

export const SocialWidget = ({
  width,
  height,
  standAlone,
  socialType,
  setLastWidgetUpdateAt,
}: GridWidget & {
  search?: string;
  socialType: 'News' | 'Twitter' | 'Youtube' | 'Reddit' | 'TechnicalIndicator';
}) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { id: strategyId } = useParams();

  const { strategies } = useUserStrategies();

  const isStrategiesPage = pathname.includes('strategies');

  const searchType = useMemo(
    () =>
      isStrategiesPage
        ? [...SEARCH_TYPE].filter((_, i) => i !== 1)
        : [...SEARCH_TYPE].slice(1),
    [pathname]
  );

  const PostCard = useMemo(
    () =>
      socialType === 'News'
        ? NewsCard
        : socialType === 'Twitter'
        ? TwitterCard
        : socialType === 'Youtube'
        ? YoutubeCard
        : socialType === 'Reddit'
        ? RedditCard
        : () => <></>,
    [socialType]
  );

  const [searchAssets, setSearchAssets] = useState<IAsset[]>([]);
  const [disabledTags, setDisabledTags] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<SocialTag[]>([]);
  const [typology, setTypology] = useState<(typeof SEARCH_TYPE)[number]>(
    searchType[0]
  );

  const handleDisabledTags = (assetId: string) => {
    setDisabledTags((pr) =>
      pr.includes(assetId)
        ? pr.filter((el) => el !== assetId)
        : [...pr, assetId]
    );
  };

  const removeSelectedTags = () => {
    setSelectedTags([]);
    setSearchAssets([]);
  };

  const handleStrategyTags = () => {
    const newTags = Object.entries(
      strategies.reduce((obj, { id, strategyAssets: { items } }) => {
        id === strategyId &&
          items.forEach(
            ({
              asset: {
                id: assetId,
                symbol,
                name,
                history: [{ usd: price }],
              },
              type,
              unit,
            }) => {
              if (type !== 'USER') {
                obj[symbol] = obj[symbol]
                  ? {
                      ...obj[symbol],
                      ...(obj[symbol].value < price * unit && {
                        id: assetId,
                        name,
                        value: price * unit,
                      }),
                    }
                  : { id: assetId, value: price * unit, name };
              }
            }
          );

        return obj;
      }, {} as { [key: string]: { id: string; value: number; name: string } })
    )
      .sort(([_, a], [__, b]) => a.value - b.value)
      .map(([symbol, { id, name }]) => ({ id, symbol, name }))
      .slice(0, 5);

    setSelectedTags(newTags);
  };

  useEffect(() => {
    isStrategiesPage && typology === 'PORTFOLIO' && handleStrategyTags();
  }, [isStrategiesPage, typology, strategies]);

  const {
    loading: loadingFilteredAssets,
    data: { getFilteredAssets: { items = [] } = {} } = {},
  } = useQuery<{
    getFilteredAssets: {
      id: string;
      items: Pick<
        IAsset,
        'id' | 'exchange' | 'logo' | 'symbol' | 'name' | 'type'
      >[];
    };
  }>(GET_FILTERED_ASSETS, {
    fetchPolicy: 'network-only',
    skip: typology === 'PORTFOLIO',
    variables: { filter: typology },
    onCompleted: ({ getFilteredAssets: { items } }) =>
      setSelectedTags(
        items.map(({ symbol, id, name }) => ({ id, symbol, name }))
      ),
  });

  const searchTags = selectedTags.reduce(
    (arr, tag) =>
      disabledTags.includes(tag.id) || tag.symbol.length === 0
        ? arr
        : [...arr, tag],
    [] as SocialTag[]
  );

  const {
    data: { getNews: { items: news = [] } = {} } = {},
    loading: loadingNews,
  } = useQuery<DataNews>(GET_NEWS, {
    fetchPolicy: 'network-only',
    pollInterval: MINUTE,
    skip: socialType !== 'News' || searchTags.length === 0,
    variables: {
      tickers: searchTags.map(
        (tag) => `${tag?.symbol.toUpperCase()}:${tag?.name.toUpperCase()}`
      ),
    },
    onCompleted: () => setLastWidgetUpdateAt?.(new Date()),
  });

  const {
    loading: loadingTwitter,
    data: { widgetTwitter: { tweets = [] } = {} } = {},
  } = useQuery<DataTwitter>(WIDGET_TWITTER, {
    fetchPolicy: 'network-only',
    pollInterval: 5 * MINUTE,
    skip: socialType !== 'Twitter' || searchTags.length === 0,
    variables: { search: searchTags.map((tag) => '$' + tag.symbol) },
    onCompleted: () => setLastWidgetUpdateAt?.(new Date()),
  });

  const {
    loading: loadingYoutube,
    data: { widgetYoutube: { items: youtubeNews = [] } = {} } = {},
  } = useQuery<DataYoutube>(WIDGET_YOUTUBE_SEARCH, {
    fetchPolicy: 'network-only',
    pollInterval: 5 * MINUTE,
    skip: socialType !== 'Youtube' || searchTags.length === 0,
    variables: { search: searchTags.map((tag) => tag.symbol) },
    onCompleted: () => setLastWidgetUpdateAt?.(new Date()),
  });

  const {
    loading: loadingReddit,
    data: { widgetReddit: { posts: redditNews = [] } = {} } = {},
  } = useQuery<DataReddit>(WIDGET_REDDIT, {
    fetchPolicy: 'network-only',
    pollInterval: 5 * MINUTE,
    skip: socialType !== 'Reddit' || searchTags.length === 0,
    variables: { search: searchTags.map((tag) => tag.symbol) },
    onCompleted: () => setLastWidgetUpdateAt?.(new Date()),
  });

  const loading =
    loadingNews ||
    loadingFilteredAssets ||
    loadingTwitter ||
    loadingYoutube ||
    loadingReddit;

  const posts =
    socialType === 'News'
      ? news
      : socialType === 'Twitter'
      ? tweets
      : socialType === 'Youtube'
      ? youtubeNews
      : socialType === 'Reddit'
      ? redditNews
      : [];

  const handleSearchItemClick = (asset: IAsset) => {
    const { id, symbol, name } = asset;
    const alreadyExistAsset = searchAssets.some(({ id }) => id === asset.id);

    !alreadyExistAsset &&
      setSelectedTags([...selectedTags, { id, symbol, name }]);
    !alreadyExistAsset && setSearchAssets((pr) => [...pr, asset]);
  };

  const filteredAssets = Object.values(
    [
      ...(typology === 'PORTFOLIO'
        ? // get strategies assets
          Object.values(
            strategies
              .find(({ id }) => id === strategyId)!
              .strategyAssets.items.reduce(
                (obj, { asset, type }) =>
                  type === 'USER'
                    ? obj
                    : {
                        ...obj,
                        [asset.id]: obj[asset.id] ?? asset,
                      },
                {} as { [key: string]: IAsset }
              )
          )
        : items),
      ...searchAssets,

      // remove duplicates and filter
    ].reduce(
      (obj, asset) =>
        selectedTags.some(({ id: tagId }) => tagId === asset.id) &&
        !disabledTags.includes(asset.id)
          ? {
              ...obj,
              [asset.id]: obj[asset.id] ?? asset,
            }
          : obj,
      {} as { [key: string]: IAsset }
    )
  );

  return (
    <>
      {!standAlone && (
        <div>
          <SearchAssetSuggestions
            onSuggestionClick={handleSearchItemClick}
            className={'focus-within:border-grey-50'}
          />

          <div
            className={`
              flex border-b-1 border-grey-10 dark:border-grey-80
            `}
          >
            <div className={'flex w-full h-16'}>
              <DropdownMenu
                dropdownClassName={`
                  flex
                  items-center
                  border-r
                  border-grey-10
                  py-5
                  pr-8
                  pl-4
                  dark:border-grey-80
                `}
                dropdownTooltipClassName={`
                  bg-white
                  border-solid
                  border
                  border-grey-10
                  -mt-3
                  ml-10
                `}
                dropdownTooltipXOffset={8}
                dropdownTooltipYOffset={24}
                dropdownArrowClassName="-right-7 text-black"
                dropdownItems={searchType.map((s) =>
                  t(`widget.social.searchType.${s}`)
                )}
                dropdownItemsData={[...searchType]}
                showArrow
                arrowPosition="right"
                handleClick={(item, data) => {
                  setTypology(data);
                  handleStrategyTags();
                }}
              >
                {t(`widget.social.searchType.${typology}`)}
              </DropdownMenu>
              <div className={'flex items-center flex-1 w-full'}>
                {loadingFilteredAssets ? (
                  <div
                    className={'flex justify-center w-full h-full items-center'}
                  >
                    <Loading size={'large'} status={true} />
                  </div>
                ) : (
                  <ScrollArrows className="w-full">
                    {selectedTags.map(({ id, symbol }) => (
                      <Tag
                        key={id}
                        disabled={disabledTags.includes(id)}
                        onClick={() => {
                          handleDisabledTags(id);
                        }}
                      >
                        {symbol}
                      </Tag>
                    ))}
                  </ScrollArrows>
                )}
                {selectedTags.length > 0 && (
                  <p
                    className={`
                      text-sm
                      text-grey-50
                      flex-shrink-0
                      border-l
                      border-grey-10
                      h-full
                      flex
                      items-center
                      p-4
                      cursor-pointer
                    `}
                    onClick={removeSelectedTags}
                  >
                    Remove all tags
                  </p>
                )}
              </div>
            </div>
          </div>
          {/*)}*/}
        </div>
      )}

      {loading ? (
        <div className={'flex justify-center w-full h-full items-center'}>
          <Loading size={'large'} status={true} />
        </div>
      ) : socialType === 'TechnicalIndicator' ? (
        <div className={`w-full h-full flex flex-col overflow-y-scroll`}>
          <div
            className={`
              w-full
              h-full
              grid
              gap-4
              grid-cols-${width}
            `}
          >
            {filteredAssets
              .filter(
                ({ type }) =>
                  type !== 'USER' && type !== 'NFT' && type !== 'ESTATE'
              )
              .map((asset) => (
                <MarketTechnicalIndicator
                  key={asset.id}
                  asset={asset}
                  height={height}
                />
              ))}
          </div>
        </div>
      ) : posts.length === 0 ? (
        <NoSearchResults />
      ) : (
        <div className={'overflow-y-scroll'}>
          <div className={`grid px-4 py-4 gap-4 grid-cols-${width}`}>
            {posts.map((post) => (
              <PostCard post={post} key={post.id} />
            ))}
          </div>
        </div>
      )}
    </>
  );
};
